/**
 * FCPX-startpakket op de draaidagkaart (fase 1): één rustige regel met de status, en pas op
 * verzoek de details en de acties. Alle rechten bepaalt de server (job.rechten); hier alleen tonen.
 *
 * Vangnetten (policies/ff-design.md): elke actie raakt de NAS of sluit een stap af en is niet
 * terug te draaien, dus steeds een bevestiging met het gevolg in de tekst.
 */
import { useCallback, useEffect, useState } from 'react';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { T, Icon, stitchOutlineBtn } from './stitch';
import { authFetch } from '../../../utils/authHelpers';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { bevestig } from '../../../utils/bevestig';
import {
  MEDIA_STAPPEN, brugTekst, formatBytes, inventarisTekst, jobToon, kopTekst, onbewezen, pakketTekst,
  transcriptieLabel,
} from '../../../utils/startpakket';
import StartpakketClips from './StartpakketClips';

async function post(pad, body) {
  const r = await authFetch(`${BACKEND_URL}/Api/startpakket${pad}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || data.error || `Mislukt (${r.status})`);
  return data;
}

/** Eén keer per projectpagina ophalen; geeft {aan, perDraaidag, brug, vernieuw}. */
export function useStartpakket(projectId) {
  const [staat, setStaat] = useState({ aan: false, perDraaidag: {}, brug: null });
  const vernieuw = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/startpakket/project/${encodeURIComponent(projectId)}`);
      if (!r.ok) return;
      const d = await r.json();
      const perDraaidag = {};
      (d.jobs || []).forEach((j) => { perDraaidag[j.draaidagId] = j; });
      setStaat({ aan: !!d.aan, perDraaidag, brug: d.brug || null });
    } catch {
      /* stil: het startpakket is een bijzaak op deze pagina */
    }
  }, [projectId]);
  useEffect(() => { vernieuw(); }, [vernieuw]);
  return { ...staat, vernieuw };
}

const kleur = { klaar: T.emerald, aandacht: T.amber, rustig: T.outline };

function Knop({ children, onClick, bezig }) {
  return (
    <Box component="button" type="button" disabled={bezig} onClick={onClick}
      sx={{ ...stitchOutlineBtn, py: 0.4, px: 1.25, fontSize: '0.75rem', opacity: bezig ? 0.6 : 1 }}>
      {children}
    </Box>
  );
}

export default function StartpakketRegel({ draaidagId, projectNummer, job, aan, brug, onVernieuw }) {
  const [open, setOpen] = useState(false);
  const [clipsOpen, setClipsOpen] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [klant, setKlant] = useState('');
  const [naam, setNaam] = useState(projectNummer ? `${projectNummer} ` : '');

  const doe = async (fn, gelukt) => {
    setBezig(true);
    try {
      await fn();
      if (gelukt) toast.success(gelukt);
      await onVernieuw?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBezig(false);
    }
  };

  if (!job) {
    if (!aan) return null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
        <Knop bezig={bezig} onClick={async () => {
          const ok = await bevestig({
            titel: 'FCPX-project klaarzetten?',
            tekst: 'De NAS-brug zoekt de projectmap, vult de standaardmappen aan en wacht op het OffShoot-log. Er wordt niets verwijderd of overschreven.',
            knop: 'Klaarzetten',
          });
          if (ok) doe(() => post('/aanvragen', { draaidagId, bron: 'tool' }), 'Startpakket aangevraagd');
        }}>
          <Icon name="movie" size={14} /> FCPX-project klaarzetten
        </Knop>
      </Box>
    );
  }

  const toon = jobToon(job);
  const rechten = job.rechten || {};
  const brugMelding = brugTekst(brug);
  const nietBewezen = onbewezen(job);

  const allesBinnen = async () => {
    const accepteer = rechten.beheren && nietBewezen.length > 0;
    const ok = await bevestig({
      titel: 'Alles binnen?',
      tekst: `${inventarisTekst(job)}${accepteer ? `\n\nJe accepteert hiermee ${nietBewezen.length} bron(nen) zonder verificatie (op jouw naam).` : ''}`,
      knop: 'Ja, alles is binnen',
    });
    if (ok) doe(() => post(`/jobs/${job.id}/alles-binnen`, { bevestig: true, accepteerOnbewezen: accepteer }), 'Vastgelegd');
  };

  return (
    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${T.surfaceLow}` }}>
      <Box component="button" type="button" onClick={() => setOpen((o) => !o)}
        sx={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
        <Icon name="movie" size={16} sx={{ color: kleur[toon] }} />
        <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 600 }}>Startpakket</Typography>
        <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: kleur[toon] }}>
          {job.gestopt ? 'Gestopt' : kopTekst(job)}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Icon name={open ? 'expand_less' : 'expand_more'} size={16} sx={{ color: T.outline }} />
      </Box>

      {brugMelding && !job.gestopt && job.ingestStatus !== 'media_compleet' && (
        <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.secondary, display: 'block', mt: 0.5 }}>
          {brugMelding}
        </Typography>
      )}

      {open && (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {job.statusReden && (
            <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.secondary }}>{job.statusReden}</Typography>
          )}
          {job.projectmap && (
            <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>
              Map: {job.projectmap.relPad}
            </Typography>
          )}

          {job.ingestStatus === 'map_kiezen' && rechten.beheren && (job.keuzes || []).map((m) => (
            <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontFamily: T.fontBody }}>{m.klant}/{m.naam}</Typography>
              <Knop bezig={bezig} onClick={async () => {
                const ok = await bevestig({ titel: 'Deze map gebruiken?', tekst: `De NAS-brug vult in ${m.klant}/${m.naam} alleen ontbrekende standaardmappen aan.`, knop: 'Gebruiken' });
                if (ok) doe(() => post(`/jobs/${job.id}/map`, { mapId: m.id }), 'Map gekozen');
              }}>Kies</Knop>
            </Box>
          ))}

          {job.ingestStatus === 'map_aanmaken' && rechten.beheren && !job.nieuweMap && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField select size="small" label="Klantmap" value={klant} onChange={(e) => setKlant(e.target.value)} sx={{ minWidth: 180 }}>
                {(job.klantmappen || []).map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Nieuwe projectmap" value={naam} onChange={(e) => setNaam(e.target.value)} sx={{ minWidth: 220 }} />
              <Knop bezig={bezig || !klant || !naam.trim()} onClick={async () => {
                const ok = await bevestig({ titel: 'Projectmap aanmaken op de NAS?', tekst: `De NAS-brug maakt ${klant}/${naam.trim()} aan. Hernoemen of verwijderen kan de brug niet; dat doet daarna alleen een mens.`, knop: 'Aanmaken' });
                if (ok) doe(() => post(`/jobs/${job.id}/map`, { klantmap: klant, naam: naam.trim() }), 'Map aangevraagd');
              }}>Aanmaken</Knop>
            </Box>
          )}
          {job.ingestStatus === 'map_aanmaken' && !rechten.beheren && (
            <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.secondary }}>De PL kiest of maakt de projectmap.</Typography>
          )}

          {(job.bronnen || []).map((b) => (
            <Box key={b.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name={b.ok ? 'check_circle' : b.probleem ? 'error' : 'radio_button_unchecked'} size={14}
                sx={{ color: b.ok ? T.emerald : b.probleem ? T.amber : T.outline }} />
              <Typography variant="caption" sx={{ fontFamily: T.fontBody }}>
                {b.naam} · {b.aantalBestanden} bestanden · {formatBytes(b.totaalBytes)}
                {b.ok ? '' : b.probleem ? ` · ${b.logStatus}${b.ontbrekend ? `, ${b.ontbrekend} ontbrekend` : ''}` : ' · niet geverifieerd'}
              </Typography>
              {!b.ok && !b.probleem && rechten.beheren && MEDIA_STAPPEN.includes(job.ingestStatus) && (
                <Knop bezig={bezig} onClick={async () => {
                  const ok = await bevestig({ titel: 'Bron accepteren?', tekst: `Je accepteert ${b.naam} zonder verificatie, op jouw naam. Doe dit alleen als je weet dat de kopie compleet is.`, knop: 'Accepteren' });
                  if (ok) doe(() => post(`/jobs/${job.id}/bronnen/${b.id}/accepteren`), 'Bron geaccepteerd');
                }}>Accepteer</Knop>
              )}
            </Box>
          ))}

          {job.ingestStatus === 'media_compleet' && !job.gestopt && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.secondary }}>
                {job.media ? `${job.media.totaal} clips gelezen · ${job.media.interview} interview${job.media.twijfel ? ` · ${job.media.twijfel} twijfel` : ''} · ` : ''}
                {transcriptieLabel(job.transcriptieStatus)}
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: job.pakketStatus === 'klaar' ? T.emerald : job.pakketStatus === 'fout' ? T.error : T.secondary }}>
                {pakketTekst(job)}
              </Typography>
              {job.pakketStatus === 'klaar' && job.pakketRelPad && (
                <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, overflowWrap: 'anywhere' }}>
                  NAS: {job.pakketRelPad} — open alleen een versie met KLAAR.json
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                {job.detectieStatus === 'klaar' && (
                  <Knop bezig={bezig} onClick={() => setClipsOpen(true)}>Clips en transcripten</Knop>
                )}
                {rechten.beheren && ['bezig', 'deels'].includes(job.transcriptieStatus) && job.pakketStatus === 'wacht' && (
                  <Knop bezig={bezig} onClick={async () => {
                    const ok = await bevestig({ titel: 'Transcriptie overslaan?', tekst: 'Het startpakket wordt nu gebouwd met de transcripten die er al zijn. Ontbrekende interviews kun je later opnieuw laten transcriberen en in een nieuwe versie zetten.', knop: 'Overslaan' });
                    if (ok) doe(() => post(`/jobs/${job.id}/transcriptie-overslaan`), 'Pakket wordt gebouwd');
                  }}>Niet wachten op transcripten</Knop>
                )}
                {rechten.beheren && ['klaar', 'fout'].includes(job.pakketStatus) && (
                  <Knop bezig={bezig} onClick={async () => {
                    const ok = await bevestig({ titel: 'Nieuwe pakketversie maken?', tekst: `De NAS-brug zet een nieuwe versie (v${(job.pakketVersie || 0) + 1}) naast de oude in 05_Startpakket. De oude versie blijft staan; importeer alleen de nieuwe.`, knop: 'Nieuwe versie' });
                    if (ok) doe(() => post(`/jobs/${job.id}/pakket-opnieuw`), 'Nieuwe versie wordt gebouwd');
                  }}>Nieuwe versie</Knop>
                )}
              </Box>
            </Box>
          )}
          {clipsOpen && (
            <StartpakketClips job={job} open={clipsOpen} onClose={() => setClipsOpen(false)} onVernieuw={onVernieuw} />
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {MEDIA_STAPPEN.includes(job.ingestStatus) && rechten.allesBinnen && !job.gestopt && (
              <Knop bezig={bezig} onClick={allesBinnen}>Alles binnen</Knop>
            )}
            {rechten.beheren && !job.gestopt && job.ingestStatus !== 'media_compleet' && (
              <Knop bezig={bezig} onClick={async () => {
                const ok = await bevestig({ titel: 'Startpakket stoppen?', tekst: 'De NAS-brug doet daarna niets meer voor deze draaidag. Wat al op de NAS staat, blijft staan.', knop: 'Stoppen', gevaarlijk: true });
                if (ok) doe(() => post(`/jobs/${job.id}/stoppen`), 'Gestopt');
              }}>Stoppen</Knop>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
